import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import Popup from "../../../../components/Popup/Popup";
import { formatDatetimeLocalValue, isPast } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { Partner } from "../PartnersModal/PartnersModal.d";
import { PartnerInvitationModalProps, PartnerInvitedUser } from "./PartnerInvitationModal.d";
import {
  EMPTY_PARTNER_INVITED_USER,
  invitePartnerUsersToEvaluation,
  isPartnerInvitedUserValid,
} from "./PartnerInvitationModal.function";

const PartnerInvitationModal = ({
  show,
  closeModal,
  evaluationId,
  language,
  onSuccess,
  partners,
}: PartnerInvitationModalProps) => {
  const [users, setUsers] = useState<Array<PartnerInvitedUser>>([EMPTY_PARTNER_INVITED_USER]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [deadline, setDeadline] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setUsers([EMPTY_PARTNER_INVITED_USER]);
    setDeadline("");
    setSelectedPartnerId(partners?.length === 1 ? partners[0].id : null);
  }, [show, partners]);

  const _updateUser = (index: number, field: keyof PartnerInvitedUser, value: string) =>
    setUsers((previousUsers) =>
      previousUsers.map((user, userIndex) => (userIndex === index ? { ...user, [field]: value } : user))
    );

  const _removeUser = (index: number) =>
    setUsers((previousUsers) => previousUsers.filter((_, userIndex) => userIndex !== index));

  const _handleSubmit = () => {
    invitePartnerUsersToEvaluation(
      evaluationId,
      users,
      setLoading,
      () => {
        onSuccess?.();
        closeModal();
      },
      language,
      partners ? selectedPartnerId! : undefined,
      deadline || undefined
    );
  };

  const defaultSelectedPartners = useMemo(() => (partners?.length === 1 ? partners : []), [partners]);
  const deadlineValid = !deadline || !isPast(deadline);
  const canSubmit =
    users.length > 0 &&
    users.every(isPartnerInvitedUserValid) &&
    (!partners || selectedPartnerId !== null) &&
    deadlineValid;

  return (
    <Popup
      show={show}
      onClose={() => closeModal()}
      modalTitle={translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.TITLE")}
      footerContent={
        <React.Fragment>
          <DstButton
            variant="secondary"
            value={translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.CANCEL")}
            clickFunction={closeModal}
          />
          <DstButton
            loading={loading}
            disabled={loading || !canSubmit}
            value={translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.SUBMIT")}
            clickFunction={_handleSubmit}
          />
        </React.Fragment>
      }
    >
      <React.Fragment>
        {partners && (
          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.PARTNER")}</Form.Label>
            <MultiSelectDropdown
              key={`partner-select-${show}`}
              items={partners}
              language={language}
              label={
                partners?.find((partner) => partner.id === selectedPartnerId)?.name ??
                translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.PARTNER_PLACEHOLDER")
              }
              badgeItemProperty="name"
              displayItem={(partner: Partner) => <span>{partner.name}</span>}
              defaultSelectedItems={defaultSelectedPartners}
              onConfirm={(selectedPartners) => setSelectedPartnerId(selectedPartners[0]?.id ?? null)}
              isSingleSelect
              borderToggle
              btnWidth="100%"
              toggleClass="w-100"
            />
          </Form.Group>
        )}
        {users.map((user, index) => (
          <div className="d-flex align-items-end gap-2 mb-2" key={index}>
            <Form.Group className="flex-grow-1">
              {index === 0 && (
                <Form.Label>{translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.EMAIL")}</Form.Label>
              )}
              <Form.Control
                type="email"
                value={user.email}
                placeholder={translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.EMAIL")}
                onChange={(event) => _updateUser(index, "email", event.target.value)}
              />
            </Form.Group>
            <Form.Group className="flex-grow-1">
              {index === 0 && (
                <Form.Label>{translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.LAST_NAME")}</Form.Label>
              )}
              <Form.Control
                value={user.last_name}
                placeholder={translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.LAST_NAME")}
                onChange={(event) => _updateUser(index, "last_name", event.target.value)}
              />
            </Form.Group>
            <Form.Group className="flex-grow-1">
              {index === 0 && (
                <Form.Label>{translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.FIRST_NAME")}</Form.Label>
              )}
              <Form.Control
                value={user.first_name}
                placeholder={translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.FIRST_NAME")}
                onChange={(event) => _updateUser(index, "first_name", event.target.value)}
              />
            </Form.Group>
            <DstButton
              variant="light"
              btnImageBefore={<span className="dst-icon-trash-01" />}
              tooltip={translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.REMOVE_ROW")}
              disabled={users.length <= 1}
              clickFunction={() => _removeUser(index)}
              value=""
            />
          </div>
        ))}
        <DstButton
          variant="light"
          btnClass="mt-2"
          value={translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.ADD_ROW")}
          btnImageBefore={<span className="me-2 dst-icon-plus" />}
          clickFunction={() => setUsers((previousUsers) => [...previousUsers, EMPTY_PARTNER_INVITED_USER])}
        />
        <Form.Group className="mt-3">
          <Form.Label>{translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.DEADLINE")}</Form.Label>
          <Form.Control
            type="datetime-local"
            value={deadline}
            min={formatDatetimeLocalValue()}
            isInvalid={!deadlineValid}
            onChange={(event) => setDeadline(event.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            {translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.DEADLINE_PAST")}
          </Form.Control.Feedback>
          <Form.Text muted>{translate(language, "PAGES.EVALUATION.PARTNER_INVITATION_MODAL.DEADLINE_HELP")}</Form.Text>
        </Form.Group>
      </React.Fragment>
    </Popup>
  );
};

export default PartnerInvitationModal;
